/**
 * Events: upcoming cards (soonest is the hero) and the past-events archive, from
 * GET /v1/events (`status=upcoming`, and `status=complete` paged by cursor). A card's `state`,
 * `starts_at` and `ends_at` are its primary media's (the broadcast); `event_at` is the marketed
 * date; `venue_name` / `location` are the venue line.
 */
import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';
import { api, unwrap } from '../../api';
import { imageUrl, imageSrcSet } from '../../api/image';
import GeoUnavailable from '../common/GeoUnavailable';
import LoadingSpinner from '../common/LoadingSpinner';
import WatchButton from '../common/WatchButton';
import { FALLBACK_IMAGE_SRC } from '../../utils/ImageUtils';
import './EventsPage.css';

const UPCOMING_LIMIT = 100;
const PAST_LIMIT = 24;

const listEvents = query => unwrap(api.GET('/v1/events', { params: { query } }));

export const formatEventDate = iso =>
  iso ? moment.utc(iso).local().format('dddd, MMMM D, YYYY · h:mm A') : '';

const eventPath = event => `/events/${event.slug || event.id}`;
const venueLine = event => [event.venue_name, event.location].filter(Boolean).join(' · ');
// Soonest first; an event with no date yet goes last, never into the hero.
const soonestFirst = events =>
  [...events].sort(
    (a, b) => (Date.parse(a.starts_at) || Infinity) - (Date.parse(b.starts_at) || Infinity),
  );

export const EventCard = ({ event }) => {
  const asset = event.art?.landscape || event.art?.portrait;
  const geoBlocked = event.geo_ok === false;
  const date = event.event_at || event.starts_at;
  const venue = venueLine(event);
  return (
    <Link to={eventPath(event)} className="block channel-card">
      <div className="relative overflow-hidden rounded-lg">
        {geoBlocked && <GeoUnavailable />}
        <img
          src={imageUrl(asset, '16x9', 480) || FALLBACK_IMAGE_SRC}
          srcSet={imageSrcSet(asset, '16x9', 960)}
          sizes="480px"
          alt={event.title?.value}
          className={`object-cover w-full aspect-video${geoBlocked ? ' opacity-40 grayscale' : ''}`}
          loading="lazy"
        />
      </div>
      <div className="channel-card-meta">
        <h3 className="channel-card-title">{event.title?.value}</h3>
        {date && (
          <p className="channel-card-date">{moment.utc(date).local().format('MMM D, YYYY')}</p>
        )}
        {venue && (
          <p className="channel-card-date" data-testid="event-venue">
            {venue}
          </p>
        )}
      </div>
    </Link>
  );
};

const FeaturedEventHero = ({ event, onZero }) => {
  const navigate = useNavigate();
  const asset = event.art?.landscape;
  const venue = venueLine(event);
  return (
    <div className="events-hero">
      <div className="events-hero-bg">
        {asset && (
          <img
            src={imageUrl(asset, '16x9', 1920)}
            srcSet={imageSrcSet(asset, '16x9', 2560)}
            sizes="100vw"
            alt={event.title?.value}
            className="events-hero-bg-image"
          />
        )}
        <div className="events-hero-overlay" />
      </div>
      <div className="events-hero-content">
        <div className="events-hero-badge">
          <span className="events-hero-badge-dot" />
          {event.state === 'LIVE' ? 'LIVE NOW' : 'NEXT EVENT'}
        </div>
        <h1 className="events-hero-title">{event.title?.value}</h1>
        <div className="events-hero-meta">
          {event.starts_at && (
            <div className="events-hero-meta-item">
              <span>{formatEventDate(event.starts_at)}</span>
            </div>
          )}
          {venue && (
            <div className="events-hero-meta-item">
              <span>{venue}</span>
            </div>
          )}
        </div>
        <div className="events-hero-actions">
          {/* The card's state is its primary media's; the event page holds the player's button. */}
          <WatchButton
            item={event}
            returnTo={eventPath(event)}
            onPlay={() => navigate(eventPath(event))}
            onZero={onZero}
          />
          <Link to={eventPath(event)} className="events-hero-btn-secondary">
            Learn More
          </Link>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, events, children }) => (
  <section className="events-section">
    <div className="events-section-header">
      <div className="events-section-header__left">
        <h2 className="events-section-header__title">{title}</h2>
      </div>
    </div>
    <div className="events-grid relative">
      {events.map(ev => (
        <div key={ev.id} className="events-grid__item">
          <EventCard event={ev} />
        </div>
      ))}
    </div>
    {children}
  </section>
);

const EventsPage = () => {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    Promise.all([
      listEvents({ status: 'upcoming', limit: UPCOMING_LIMIT }),
      listEvents({ status: 'complete', limit: PAST_LIMIT }),
    ])
      .then(([up, done]) => {
        if (!active) {
          return;
        }
        // The list is newest first; the next event is the soonest upcoming one.
        setUpcoming(soonestFirst(up.data));
        setPast(done.data);
        setCursor(done.page?.next_cursor || null);
      })
      .catch(err => {
        console.error('EventsPage: GET /v1/events failed:', err);
        if (active) {
          setError("We couldn't load events right now. Please try again.");
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  // The hero's countdown reached zero: an event card is not in /v1/live, so ask the list again.
  const refreshUpcoming = useCallback(
    () =>
      listEvents({ status: 'upcoming', limit: UPCOMING_LIMIT })
        .then(up => setUpcoming(soonestFirst(up.data)))
        .catch(err => console.error('EventsPage: refreshing upcoming events failed:', err)),
    [],
  );

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const body = await listEvents({ status: 'complete', limit: PAST_LIMIT, cursor });
      setPast(prev => [...prev, ...body.data]);
      setCursor(body.page?.next_cursor || null);
    } catch (err) {
      console.error('EventsPage: loading more past events failed:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [cursor]);

  if (loading) {
    return <LoadingSpinner message="Loading events..." />;
  }

  const hero = upcoming[0];

  return (
    <div className="min-h-screen bg-primary text-white">
      {hero && <FeaturedEventHero event={hero} onZero={refreshUpcoming} />}

      <div className="container mx-auto pb-16 events-content">
        {error && <p className="py-20 text-center text-text-muted">{error}</p>}

        {upcoming.length > 0 && <Section title="Upcoming Events" events={upcoming} />}

        {past.length > 0 && (
          <Section title="Past Events" events={past}>
            {cursor && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  className="events-hero-btn-secondary"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </Section>
        )}

        {!error && upcoming.length === 0 && past.length === 0 && (
          <div className="text-center py-20">
            <p className="text-text-muted text-lg font-body">No events available at this time.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
